import { useMemo, useState } from 'react';

import { EventDialog } from './event-dialog';
import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useGetProceduralTimelineUploadEligibility } from '@/api/queries/useGetProceduralTimelineUploadEligibility';
import { PermissionGate, usePermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface CreateEventButtonProps {
  matterId: string;
  events: ProceduralTimelineEvent[];
}

export function CreateEventButton({ matterId, events }: CreateEventButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { allowed: canEdit } = usePermission(Permission.ProceduralTimelineEdit);
  const { data: eligibility } = useGetProceduralTimelineUploadEligibility(matterId, canEdit);

  const isExtractionInFlight = eligibility?.canUpload === false && eligibility.blockedBy === 'extraction_in_flight';

  const eventsById = useMemo(() => {
    return Object.fromEntries(events.map((e) => [e.id, e]));
  }, [events]);

  const button = (
    <Button onClick={() => setIsOpen(true)} disabled={isExtractionInFlight}>
      <Icon className="lucide--plus" />
      Create Event
    </Button>
  );

  return (
    <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
      {isExtractionInFlight ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>
            A timetable is currently being processed. Please wait for it to finish before creating events manually.
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
      <EventDialog event={null} eventsById={eventsById} matterId={matterId} open={isOpen} onOpenChange={setIsOpen} />
    </PermissionGate>
  );
}
